import {
  useDataQuery,
  useDataQueryClient,
} from "@Libs/DataLayer/hooks/useDataQuery";
import { useDataMutation } from "@Libs/DataLayer/hooks/useDataMutation";
import { useIsMutating } from "react-query";

import {
  IUpdateDraftPayload,
  getServiceRequestDraft,
  getTaskCreationConfig,
  updateServiceRequest,
  updateServiceRequestDraft,
} from "../services/serviceRequestCreation";
import { IDraftIdentity } from "./useDraftIdentity";
import {
  IPhotoAttachment,
  IServiceRequestDraft,
  ITaskCreationConfig,
} from "../types";
import { mergePhotoPreviews } from "../utils/photoPreviews";

/**
 * One cache key per resource. Every component that needs the draft reads this
 * same entry, so writing the PUT response into it updates the screen, the
 * progress bar and the summary from a single write — no fan-out, no context.
 */
export const DRAFT_QUERY_KEY = "serviceRequestDraft";
export const CONFIG_QUERY_KEY = "taskCreationConfig";
export const AI_SUGGESTION_QUERY_KEY = "aiSuggestedSCategory";

export const draftQueryKey = (draftKey: string) => [
  DRAFT_QUERY_KEY,
  draftKey,
];

export const configQueryKey = (sCategoryId: number) => [
  CONFIG_QUERY_KEY,
  sCategoryId,
];

export const aiSuggestionQueryKey = (publicToken: string) => [
  AI_SUGGESTION_QUERY_KEY,
  publicToken,
];

/**
 * The request the flow is filling in.
 *
 * Edit mode never fetches: Rails already serialized the task into the page, so
 * it seeds the cache and the public draft endpoint is not involved at all.
 */
export const useServiceRequestDraft = (
  {
    draftKey,
    isEditing,
    initialDraft,
  }: Pick<IDraftIdentity, "draftKey" | "isEditing" | "initialDraft">,
  { enabled = true }: { enabled?: boolean } = {}
) =>
  useDataQuery<IServiceRequestDraft>(
    draftQueryKey(draftKey ?? ""),
    () => getServiceRequestDraft(draftKey as string),
    isEditing
      ? { initialData: initialDraft, enabled: false }
      : { enabled: enabled && Boolean(draftKey) }
  );

export const useTaskCreationConfig = (sCategoryId: number | undefined) =>
  useDataQuery<ITaskCreationConfig>(
    configQueryKey(sCategoryId ?? 0),
    () => getTaskCreationConfig(sCategoryId as number),
    { enabled: Boolean(sCategoryId), staleTime: Infinity }
  );

/**
 * Saves one screen.
 *
 * Tagged with a shared mutation key so any component can ask whether a save is
 * in flight via `useIsDraftSaving`, which is what disables both CTAs.
 */
export const useSaveScreen = ({
  draftKey,
  isEditing,
  taskId,
}: Pick<IDraftIdentity, "draftKey" | "isEditing" | "taskId">) => {
  const queryClient = useDataQueryClient();

  return useDataMutation(
    ({ validate, ...payload }: IUpdateDraftPayload & { validate?: boolean }) =>
      isEditing
        ? updateServiceRequest(taskId as number, payload)
        : updateServiceRequestDraft(draftKey as string, payload, { validate }),
    {
      mutationKey: DRAFT_QUERY_KEY,
      onSuccess: (draft) => {
        // `ServiceRequestSerializer` always emits `photo_attachments`, and its
        // URLs point at variants a background job has not written yet — so a
        // blind overwrite here swaps every freshly uploaded photo's working
        // preview for an image that 403s. The merge keeps those previews and
        // takes the response for everything else, including which photos the
        // draft still has.
        queryClient.setQueryData<IServiceRequestDraft>(
          draftQueryKey(draftKey ?? ""),
          (current) => ({
            ...draft,
            photo_attachments: mergePhotoPreviews(
              current?.photo_attachments,
              draft.photo_attachments
            ),
          })
        );
      },
    }
  );
};

/** True while a screen is saving, readable from anywhere. */
export const useIsDraftSaving = () => useIsMutating(DRAFT_QUERY_KEY) > 0;

/**
 * Warms the config for a subcategory the user just picked.
 *
 * Called on selection change rather than on Continue, so the fetch overlaps the
 * time the user spends reading the screen. Continue then awaits the same query,
 * and only actually waits if the user beat the network.
 */
export const usePrefetchTaskCreationConfig = () => {
  const queryClient = useDataQueryClient();

  return (sCategoryId: number) =>
    queryClient.prefetchQuery(
      configQueryKey(sCategoryId),
      () => getTaskCreationConfig(sCategoryId),
      { staleTime: Infinity }
    );
};

/**
 * Seeds the config cache from the description POST, which returns `form_config`
 * alongside the draft. Saves the first screen a round trip.
 */
export const useSeedTaskCreationConfig = () => {
  const queryClient = useDataQueryClient();

  return (config: ITaskCreationConfig) =>
    queryClient.setQueryData(
      configQueryKey(config.category.s_category_id),
      config
    );
};

/**
 * Seeds the AI's suggested subcategory from the description POST (RT-4384) —
 * the only place it is ever returned. Unlike `form_config`, there is no field
 * for it on the draft itself and no endpoint to recover it on a refetch, so a
 * hard refresh on the subcategory screen loses the suggestion.
 */
export const useSeedAiSuggestedSCategory = () => {
  const queryClient = useDataQueryClient();

  return (publicToken: string, suggestedSCategoryId: number) =>
    queryClient.setQueryData(
      aiSuggestionQueryKey(publicToken),
      suggestedSCategoryId
    );
};

/** The AI's suggested subcategory for this draft, if the description POST
 * returned one — read-only, there is nothing to fetch it from. */
export const useAiSuggestedSCategoryId = (publicToken: string | null) => {
  const queryClient = useDataQueryClient();

  return publicToken
    ? queryClient.getQueryData<number>(aiSuggestionQueryKey(publicToken))
    : undefined;
};

/**
 * Puts the description screen's photos on the draft the POST just created.
 *
 * They were uploaded before that draft existed, so they reach the backend as
 * ids on the creation payload and come back on the response — but naming the
 * unprocessed variants `mergePhotoPreviews` exists to paper over. Seeding the
 * local previews here is what makes the photos screen show the pictures the
 * user actually picked rather than a row of broken images.
 */
export const useSeedDraftPhotos = () => {
  const queryClient = useDataQueryClient();

  return (draftKey: string, photos: IPhotoAttachment[]) => {
    if (photos.length === 0) return;

    queryClient.setQueryData<IServiceRequestDraft | undefined>(
      draftQueryKey(draftKey),
      (current) =>
        current && {
          ...current,
          // The cached draft is the POST response, so it already lists these
          // photos — this swaps their URLs, it does not invent rows.
          photo_attachments: mergePhotoPreviews(
            photos,
            current.photo_attachments
          ),
        }
    );
  };
};

/**
 * Merges a freshly uploaded photo straight into the cached draft, so its
 * thumbnail survives navigating away from the photos screen and back.
 *
 * The screen's own Continue save never carries it: `toDraftFields` puts
 * `picture_upload`'s value into `answers` as `{id, description}` only, never
 * a URL, so `draft.photo_attachments` would otherwise stay exactly as it was
 * when the draft was first fetched — missing every photo uploaded since.
 */
export const useRecordUploadedPhoto = (draftKey: string) => {
  const queryClient = useDataQueryClient();

  return (photo: IPhotoAttachment) =>
    queryClient.setQueryData<IServiceRequestDraft | undefined>(
      draftQueryKey(draftKey),
      (current) =>
        current && {
          ...current,
          photo_attachments: [...(current.photo_attachments ?? []), photo],
        }
    );
};

/**
 * Puts a caption the attachment has just accepted into the cached draft.
 *
 * The attachment is where a caption is stored, but the draft is what this
 * screen re-reads when it is left and re-entered, and it holds the copy it was
 * fetched with. Without this write the field would come back empty until the
 * next fetch, for a caption the server already has.
 */
export const useRecordPhotoDescription = (draftKey: string) => {
  const queryClient = useDataQueryClient();

  return (id: number, description: string) =>
    queryClient.setQueryData<IServiceRequestDraft | undefined>(
      draftQueryKey(draftKey),
      (current) =>
        current && {
          ...current,
          photo_attachments: (current.photo_attachments ?? []).map((photo) =>
            photo.id === id ? { ...photo, description } : photo
          ),
        }
    );
};

/** The other half of `useRecordUploadedPhoto` — drops a removed photo from
 * the same cached list. */
export const useForgetUploadedPhoto = (draftKey: string) => {
  const queryClient = useDataQueryClient();

  return (id: number) =>
    queryClient.setQueryData<IServiceRequestDraft | undefined>(
      draftQueryKey(draftKey),
      (current) =>
        current && {
          ...current,
          photo_attachments: (current.photo_attachments ?? []).filter(
            (photo) => photo.id !== id
          ),
        }
    );
};
